/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
/**
 * `entities/brief` TanStack Query key factory.
 *
 * Tuple shapes are pinned by `query-keys.test.ts` (cache contract). Optional
 * fields default to `null` so omitting `search`/`sortBy`/`sortOrder` produces
 * a stable, structurally-equal key — important because TanStack uses deep
 * equality on the key to dedupe in-flight requests and reuse cache.
 *
 * Surface:
 *   - `BRIEFS_QUERY_KEYS.all`                  → `['briefs']`
 *   - `BRIEFS_QUERY_KEYS.lists(projectId)`     → `['briefs', 'list', projectId]`
 *   - `BRIEFS_QUERY_KEYS.list(params)`         → `[...lists, { page, pageSize, search?, status?, sortBy?, sortOrder? }]`
 *   - `BRIEFS_QUERY_KEYS.detail(p, b)`         → `['briefs', 'detail', projectId, briefId]`
 */
import type { BriefSortField, BriefSortOrder, BriefStatus } from '../model/types';

/**
 * Params consumed by the list-key factory. `status` participates in cache
 * identity (multi-value filter, normalized to `null` when absent) so a
 * status-filtered list is cached separately from the unfiltered one. Keep in
 * sync with `BriefsListParams`.
 */
export interface BriefsListKeyParams {
  projectId: string;
  page: number;
  pageSize: number;
  search?: string;
  status?: BriefStatus[];
  sortBy?: BriefSortField;
  sortOrder?: BriefSortOrder;
}

export const BRIEFS_QUERY_KEYS = {
  /** Root namespace for every briefs cache entry. */
  all: ['briefs'] as const,

  /** All `list` queries under a given project. Useful for `invalidateQueries`. */
  lists: (projectId: string) => [...BRIEFS_QUERY_KEYS.all, 'list', projectId] as const,

  /**
   * Paginated/searched/sorted brief list under a project. Optional fields
   * resolve to `null` so the key is structurally stable when callers omit
   * them.
   */
  list: (params: BriefsListKeyParams) =>
    [
      ...BRIEFS_QUERY_KEYS.lists(params.projectId),
      {
        page: params.page,
        pageSize: params.pageSize,
        search: params.search ?? null,
        status: params.status ?? null,
        sortBy: params.sortBy ?? null,
        sortOrder: params.sortOrder ?? null,
      },
    ] as const,

  /** Single brief by id. */
  detail: (projectId: string, briefId: string) =>
    [...BRIEFS_QUERY_KEYS.all, 'detail', projectId, briefId] as const,

  /** All briefs across all projects in a workspace (Trackers board). */
  allByWorkspace: (workspaceId: string) =>
    [...BRIEFS_QUERY_KEYS.all, 'all-by-workspace', workspaceId] as const,
} as const;
